import {
    Box, Button, Card, CardActions, CardContent, CircularProgress, FormControl,
    FormControlLabel,
    FormHelperText,
    FormLabel,
    Grid, InputLabel, MenuItem,
    Radio,
    RadioGroup,
    Select, TextField, Typography
} from '@mui/material';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';
import { apiService } from '../../api/axiosClient';
import Page from '../../components/Page';
import { InputPhoneMask } from '../../components/input-numbers';
import { ImgStyled } from '../../utils/shop';
import PasswordChangeButton from './PasswordChangeButton';

const StaffInfo = () => {
    const params = useParams();
    const navigate = useNavigate();
    const [staff, setStaff] = useState('')
    const [loading, setLoading] = useState(false)
    const [isAdmin, setIsAdmin] = useState(false)
    const [imgSrc, setImgSrc] = useState('/images/avatars/1.png')
    const {
        control,
        handleSubmit,
        formState: { errors },
        reset,
    } = useForm()

    useEffect(() => {        
        apiService.get(`/staff/${params.id}`)
        .then(response => {
            const payload = response.data;
            setStaff(payload.data)
        })        
        .catch(e => console.error(e));
    }, [params.id]);

    useEffect(() => {
        const defaultValues = {
            username: staff?.username,
            email: staff?.email,
            name: staff?.name,
            phone: staff?.phone,
            role: staff?.role,
            task_code: staff?.task_code,
            task_target: staff?.task_target,
            task_charge: staff?.task_charge,
            task_section: staff?.task_section
        }
        reset(defaultValues)

        setIsAdmin(staff?.role == 'ROLE_ADMIN' ? true : false)

    }, [reset, staff]);

    const goBack = () => {
        navigate(-1)
    }

    const onSubmit = async (data, e) => {
        setLoading(true)

        await apiService.put('/staff', staff)
        .then(response => {
            toast.success('수정 되었습니다')
            goBack()
        }).catch(error => {
            console.error(error)
        }).finally(() => {
            setLoading(false)
        });
    }

    return (
        <Page title="Staff 정보">
            <Card>
                <form onSubmit={handleSubmit(onSubmit)} id="userform">
                    <CardContent sx={{ pt: 8 }}>
                        <Box sx={{ mb: 9, textAlign: 'center' }}>
                            <Typography variant='h5' sx={{ mb: 3, lineHeight: '2rem' }}>
                                Staff 정보
                            </Typography>
                        </Box>

                        <Grid container spacing={6}>
                            <Grid item xs={12} sx={{ my: 5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <ImgStyled src={imgSrc} alt='Profile Pic' />
                                </Box>
                            </Grid>

                            <Grid item xs={12} sm={6} lg={4}>
                                <FormControl fullWidth>
                                    <Controller
                                        name='username'
                                        control={control}
                                        rules={{ required: true }}
                                        defaultValue=''
                                        render={({ field: { value, onChange } }) => (
                                            <TextField
                                                value={value}
                                                label='사용자ID'
                                                id='username'
                                                onChange={(e) => setStaff({ ...staff, username: e.target.value })}
                                                placeholder='사용자ID를 입력해주세요'
                                                error={Boolean(errors.username)}
                                                helpertext={errors?.message}
                                                aria-describedby='validation-async-username'
                                                variant="standard"
                                            />
                                        )}
                                    />
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} sm={6} lg={4}>
                                <FormControl fullWidth>
                                    <Controller
                                        name='email'
                                        control={control}
                                        rules={{ required: true }}
                                        defaultValue=''
                                        render={({ field: { value, onChange } }) => (
                                            <TextField
                                                type='email'
                                                value={value}
                                                label='Email'
                                                id='email'
                                                onChange={(e) => setStaff({ ...staff, email: e.target.value })}
                                                placeholder='사용자 이메일주소를 입력해주세요'
                                                error={Boolean(errors.email)}
                                                helpertext={errors?.message}
                                                aria-describedby='validation-async-email'
                                                variant="standard"
                                            />
                                        )}
                                    />
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} sm={6} lg={4}>
                                <FormControl fullWidth>
                                    <Controller
                                        name='name'
                                        control={control}
                                        rules={{ required: true }}
                                        defaultValue=''
                                        render={({ field: { value, onChange } }) => (
                                            <TextField
                                                value={value}
                                                label='이름'
                                                id='name'
                                                onChange={(e) => setStaff({ ...staff, name: e.target.value })}
                                                placeholder='이름을 입력해주세요'
                                                error={Boolean(errors.name)}
                                                helpertext={errors?.message}
                                                aria-describedby='validation-async-name'
                                                variant="standard"
                                            />
                                        )}
                                    />
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <FormControl fullWidth>
                                    <InputLabel htmlFor="phone">전화번호</InputLabel>
                                    <Controller
                                        name='phone'
                                        control={control}
                                        rules={{ required: true }}
                                        defaultValue=''
                                        render={({ field: { value, onChange } }) => (
                                            <InputPhoneMask
                                                mask="000-0000-0000"
                                                value={value}
                                                label='전화번호'
                                                id='phone'
                                                onChange={(e) => setStaff({ ...staff, phone: e.target.value })}
                                                placeholder='010-1234-5678'
                                                error={Boolean(errors.phone)}
                                                aria-describedby='validation-async-phone'                                                
                                                variant="standard"
                                            />
                                        )}
                                    />
                                    {errors.phone && (
                                        <FormHelperText sx={{ color: 'error.main' }} id='validation-async-phone'>
                                            필수
                                        </FormHelperText>
                                    )}
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <FormControl fullWidth disabled>
                                    <InputLabel id='role'>Role</InputLabel>
                                    <Controller                                        
                                        name='role'
                                        control={control}
                                        rules={{ required: true }}
                                        defaultValue=''
                                        render={({ field: { value, onChange } }) => (
                                            <Select
                                                label='Role'
                                                value={value}
                                                onChange={(e) => setStaff({ ...staff, role: e.target.value })}
                                                labelId='role'
                                                error={Boolean(errors.role)}
                                                helpertext={errors?.message}
                                                variant="standard"
                                            >
                                                <MenuItem value='ROLE_ADMIN'>관리자</MenuItem>
                                                <MenuItem value='ROLE_MANAGER'>매니저</MenuItem>
                                            </Select>
                                        )}
                                    />
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <FormControl fullWidth>
                                    <Controller
                                        name='task_code'
                                        control={control}
                                        rules={{ required: true }}
                                        defaultValue=''
                                        render={({ field: { value, onChange } }) => (
                                            <TextField
                                                value={value}
                                                label='담당업무코드'
                                                id='task_code'
                                                onChange={(e) => setStaff({ ...staff, task_code: e.target.value })}
                                                placeholder='업무코드를 입력해주세요'
                                                error={Boolean(errors.task_code)}
                                                helpertext={errors?.message}
                                                variant="standard"
                                            />
                                        )}
                                    />
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <FormControl fullWidth>
                                    <Controller
                                        name='task_target'
                                        control={control}
                                        rules={{ required: true }}
                                        defaultValue='0'
                                        render={({ field: { value, onChange } }) => (
                                            <TextField
                                                value={value}
                                                label='매출목표'
                                                id='task_target'
                                                onChange={(e) => setStaff({ ...staff, task_target: e.target.value })}
                                                placeholder='매출목표를 입력해주세요'
                                                error={Boolean(errors.task_target)}
                                                helpertext={errors?.message}
                                                aria-describedby='validation-async-task_target'
                                                variant="standard"
                                            />
                                        )}
                                    />
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <FormControl fullWidth>
                                    <InputLabel id='role'>담당업무</InputLabel>
                                    <Controller
                                        name='task_charge'
                                        control={control}
                                        rules={{ required: "담당업무를 선택해주세요." }}
                                        defaultValue=''
                                        render={({ field: { value, onChange } }) => (
                                            <Select
                                                label='task_charge'
                                                value={value || ''}
                                                onChange={(e) => setStaff({ ...staff, task_charge: e.target.value })}
                                                labelId='task_charge'        
                                                variant="standard"
                                            >
                                                <MenuItem value='영업'>영업</MenuItem>
                                                <MenuItem value='구매'>구매</MenuItem>
                                                <MenuItem value='마케팅'>마케팅</MenuItem>
                                            </Select>
                                        )}
                                    />
                                </FormControl>
                            </Grid>

                            <Grid item md={12}>                            
                                <FormControl component="fieldset" fullWidth>
                                    <FormLabel component="legend">업체분류</FormLabel>
                                    <Controller
                                        name="task_section"
                                        control={control}
                                        defaultValue="국가연구소"
                                        render={({ field: { value, onChange } }) => (
                                            <RadioGroup 
                                                row 
                                                value={value || "국가연구소"} // Ensure value is not null
                                                onChange={(e) => {
                                                    onChange(e.target.value); // Update the form state
                                                    setStaff({ ...staff, task_section: e.target.value }); // Update the staff state
                                                }}
                                            >
                                                <FormControlLabel value="국가연구소" control={<Radio />} label="국가연구소" />
                                                <FormControlLabel value="제조업" control={<Radio />} label="제조업" />
                                                <FormControlLabel value="학교" control={<Radio />} label="학교" />
                                                <FormControlLabel value="도소매" control={<Radio />} label="도소매" />
                                            </RadioGroup>
                                        )}
                                    />
                                </FormControl>
                            </Grid>
                        </Grid>
                    </CardContent>
                </form>

                <CardActions className='card-action'>
                    <Button type="submit" form="userform" variant='contained'>
                        {loading ? (
                            <CircularProgress
                                sx={{
                                    color: 'common.white',
                                    width: '20px !important',
                                    height: '20px !important',
                                    mr: theme => theme.spacing(2)
                                }}
                            />
                        ) : null}
                        수정
                    </Button>
                    <Button variant='outlined' color='secondary' onClick={goBack}>
                        취소
                    </Button>
                    {isAdmin && <PasswordChangeButton userid={params.id} />}
                </CardActions>
            </Card>
        </Page>
    )
}

export default StaffInfo